import { EventData } from '@/interfaces/event';
import { chatContentInfoAtom, chatCustomerAtom } from '@/stores/chat';
import im from '@/utils/im';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

export default function useCustomerEventListener() {
  const [customer, setCustomer] = useAtom(chatCustomerAtom);
  const updateChatContentInfo = useSetAtom(chatContentInfoAtom);

  const currentCustomerId = customer?.customerId;
  const currentSubsourceId = customer?.subSourceId;

  const onCusChangeAiEnable = useCallback(
    (data: EventData) => {
      const { targetId, body } = data;
      const enableAi = body.enableAi;
      if (data.targetKind !== 'customer' || targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== targetId) return prev;
        // 根据 enableAi 更新 receptionType
        const receptionType = enableAi ? 'ai_agent' : 'manual';
        return { ...prev, enableAi, receptionType };
      });
    },
    [currentCustomerId, setCustomer],
  );
  const onChannelChangeEnableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'channel' || data.targetId !== currentSubsourceId) {
        return;
      }
      if (data.body.enableAi) {
        updateChatContentInfo({ customerId: currentCustomerId! });
      } else {
        setCustomer((prev) => (prev?.subSourceId ? { ...prev, enableAi: false } : prev));
      }
    },
    [currentCustomerId, currentSubsourceId, setCustomer, updateChatContentInfo],
  );

  const onCusAutoDisableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'customer' || data.targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== currentCustomerId) return prev;
        // 只有在当前是小鼓AI接待时才切换到人工模式
        if (prev.receptionType === 'ai_agent') {
          return { ...prev, enableAi: false, receptionType: 'manual' };
        }
        return prev;
      });
    },
    [currentCustomerId, setCustomer],
  );

  const onCusDelayAutoEnableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'customer' || data.targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== currentCustomerId) return prev;
        // 只有在当前是人工接待时才切换到小鼓AI模式
        if (prev.receptionType === 'manual') {
          return { ...prev, enableAi: true, receptionType: 'ai_agent' };
        }
        return prev;
      });
    },
    [currentCustomerId, setCustomer],
  );

  const onCusChangeEnableWorkflow = useCallback(
    (data: EventData) => {
      const { targetId, body } = data;
      const enableWorkflow = body.enableWorkflow;
      if (data.targetKind !== 'customer' || targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== targetId) return prev;
        // 根据 enableWorkflow 更新 receptionType
        const receptionType = enableWorkflow ? 'workflow' : 'manual';
        return { ...prev, receptionType };
      });
    },
    [currentCustomerId, setCustomer],
  );

  useEffect(() => {
    im.on('cus_change_enable_ai', onCusChangeAiEnable);
    im.on('channel_change_enable_ai', onChannelChangeEnableAi);
    im.on('cus_auto_disable_ai', onCusAutoDisableAi);
    im.on('cus_delay_auto_enable_ai', onCusDelayAutoEnableAi);
    im.on('cus_change_enable_workflow', onCusChangeEnableWorkflow);
    return () => {
      im.off('cus_change_enable_ai', onCusChangeAiEnable);
      im.off('channel_change_enable_ai', onChannelChangeEnableAi);
      im.off('cus_auto_disable_ai', onCusAutoDisableAi);
      im.off('cus_delay_auto_enable_ai', onCusDelayAutoEnableAi);
      im.off('cus_change_enable_workflow', onCusChangeEnableWorkflow);
    };
  }, [
    onCusChangeAiEnable,
    onChannelChangeEnableAi,
    onCusAutoDisableAi,
    onCusDelayAutoEnableAi,
    onCusChangeEnableWorkflow,
  ]);
}
